import { supabase } from '../../../../lib/supabase';

import type { ParsedImportFile, ParsedOutreachRow, ParsedSheet } from './outreachImportUtils';

const CHUNK_SIZE = 50;
const DEADLOCK_RETRIES = 5;

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function isDeadlockError(code?: string, message?: string): boolean {
  return code === '40P01' || (message?.toLowerCase().includes('deadlock') ?? false);
}

function isTransportAbortError(error: { message?: string; code?: string } | null): boolean {
  if (!error) return false;
  const msg = (error.message ?? '').toLowerCase();
  return (
    msg.includes('timed out') ||
    msg.includes('aborted') ||
    msg.includes('aborterror') ||
    error.code === '20'
  );
}

let bulkOperationInFlight = false;

export interface SheetImportResult {
  sheetName: string;
  batchId: string | null;
  rowsInFile: number;
  imported: number;
  duplicates: number;
  skipped: number;
  errors: number;
}

export interface ImportRunResult {
  filename: string;
  fileFormat: string;
  replacedExisting: boolean;
  cancelled: boolean;
  rowsInFile: number;
  totalImported: number;
  totalDuplicates: number;
  totalSkipped: number;
  totalErrors: number;
  sheets: SheetImportResult[];
  databaseBefore: OutreachDatabaseCounts | null;
  databaseAfter: OutreachDatabaseCounts | null;
}

export interface ImportProgress {
  phase: 'loading_existing' | 'wiping' | 'importing' | 'done' | 'cancelled';
  processed: number;
  total: number;
  imported: number;
  duplicates: number;
  errors: number;
  currentSheet?: string;
}

export interface DeleteProgress {
  phase: 'deleting' | 'done';
  processed: number;
  total: number;
  deleted: number;
}

export interface OutreachDatabaseCounts {
  persons: number;
  with_email: number;
  import_batches: number;
  touchpoints: number;
  responses: number;
}

export interface ImportBatchRow {
  id: string;
  filename: string;
  sheet_name: string | null;
  imported_count: number;
  total_rows: number;
  created_at: string;
}

export interface ImportOptions {
  replaceAll?: boolean;
}

export interface ImportCallbacks {
  onProgress?: (progress: ImportProgress) => void;
  signal?: AbortSignal;
}

type InsertRow = ReturnType<typeof toInsertRow>;

function normalizeEmail(email: string | null | undefined): string | null {
  if (!email) return null;
  const e = email.trim().toLowerCase();
  return e || null;
}

function normalizePhone(phone: string | null | undefined): string | null {
  if (!phone) return null;
  const p = phone.replace(/\D/g, '');
  return p || null;
}

function throwIfAborted(signal?: AbortSignal) {
  if (signal?.aborted) throw new DOMException('Import cancelled', 'AbortError');
}

function dedupeWithinFile(rows: InsertRow[]): { rows: InsertRow[]; duplicates: number } {
  const seenEmail = new Set<string>();
  const seenPhone = new Set<string>();
  const out: InsertRow[] = [];
  let duplicates = 0;

  for (const row of rows) {
    const e = normalizeEmail(row.email);
    const p = normalizePhone(row.phone);
    if (e && seenEmail.has(e)) { duplicates++; continue; }
    if (p && seenPhone.has(p)) { duplicates++; continue; }
    if (e) seenEmail.add(e);
    if (p) seenPhone.add(p);
    out.push(row);
  }
  return { rows: out, duplicates };
}

function filterExisting(
  rows: InsertRow[],
  existingEmails: Set<string>,
  existingPhones: Set<string>,
): { rows: InsertRow[]; duplicates: number } {
  const out: InsertRow[] = [];
  let duplicates = 0;
  for (const row of rows) {
    const e = normalizeEmail(row.email);
    const p = normalizePhone(row.phone);
    if (e && existingEmails.has(e)) { duplicates++; continue; }
    if (p && existingPhones.has(p)) { duplicates++; continue; }
    out.push(row);
    if (e) existingEmails.add(e);
    if (p) existingPhones.add(p);
  }
  return { rows: out, duplicates };
}

async function fetchExistingKeys(): Promise<{ emails: Set<string>; phones: Set<string> }> {
  const emails = new Set<string>();
  const phones = new Set<string>();
  const pageSize = 1000;
  let from = 0;

  while (true) {
    const { data, error } = await supabase
      .from('outreach_persons')
      .select('email, phone')
      .order('id', { ascending: true })
      .range(from, from + pageSize - 1);
    if (error || !data?.length) break;
    for (const row of data) {
      const e = normalizeEmail(row.email as string | null);
      const p = normalizePhone(row.phone as string | null);
      if (e) emails.add(e);
      if (p) phones.add(p);
    }
    if (data.length < pageSize) break;
    from += pageSize;
  }
  return { emails, phones };
}

function toInsertRow(
  row: ParsedOutreachRow,
  batchId: string,
  sheet: ParsedSheet,
  fileFormat: string,
) {
  const email = normalizeEmail(row.email);
  return {
    full_name: row.full_name,
    company_name: row.company_name,
    person_kind: row.person_kind,
    email,
    phone: row.phone,
    date_of_birth: row.date_of_birth,
    gender: row.gender,
    address: row.address,
    functional_area: row.functional_area,
    specialization: row.specialization,
    industry: row.industry,
    current_salary: row.current_salary,
    seniority_level: row.seniority_level,
    location: row.location,
    current_role: row.current_role,
    skills: row.skills,
    source_row_status: row.source_row_status,
    source_followup_date: row.source_followup_date,
    import_batch_id: batchId,
    source_sheet: sheet.sheetName,
    source_channel: fileFormat === 'csv' ? 'csv_import' : 'excel_import',
    attributes: row.attributes,
    outreach_state: 'not_contacted' as const,
    is_reachable: !!email,
  };
}

interface BatchStats {
  inserted: number;
  duplicates: number;
  errors: number;
}

function parseRpcStats(data: unknown): BatchStats {
  const d = data as BatchStats | null;
  return {
    inserted: d?.inserted ?? 0,
    duplicates: d?.duplicates ?? 0,
    errors: d?.errors ?? 0,
  };
}

async function importChunkViaRpc(rows: InsertRow[]): Promise<BatchStats> {
  if (rows.length === 0) return { inserted: 0, duplicates: 0, errors: 0 };

  for (let attempt = 0; attempt < DEADLOCK_RETRIES; attempt++) {
    const { data, error } = await supabase.rpc('outreach_import_persons', { p_rows: rows });
    if (!error) return parseRpcStats(data);

    if (isDeadlockError(error.code, error.message) && attempt < DEADLOCK_RETRIES - 1) {
      await sleep(400 * (attempt + 1) + Math.random() * 200);
      continue;
    }

    if (rows.length === 1) {
      return { inserted: 0, duplicates: 0, errors: 1 };
    }

    let totals: BatchStats = { inserted: 0, duplicates: 0, errors: 0 };
    const half = Math.ceil(rows.length / 2);
    const left = await importChunkViaRpc(rows.slice(0, half));
    const right = await importChunkViaRpc(rows.slice(half));
    totals = {
      inserted: left.inserted + right.inserted,
      duplicates: left.duplicates + right.duplicates,
      errors: left.errors + right.errors,
    };
    return totals;
  }

  return { inserted: 0, duplicates: 0, errors: rows.length };
}

async function importRowsChunked(
  rows: InsertRow[],
  callbacks: ImportCallbacks | undefined,
  progressBase: { processed: number; total: number; imported: number; duplicates: number; errors: number },
  sheetName: string,
): Promise<BatchStats> {
  let totals: BatchStats = { inserted: 0, duplicates: 0, errors: 0 };

  for (let i = 0; i < rows.length; i += CHUNK_SIZE) {
    throwIfAborted(callbacks?.signal);
    const chunk = rows.slice(i, i + CHUNK_SIZE);
    const part = await importChunkViaRpc(chunk);
    totals = {
      inserted: totals.inserted + part.inserted,
      duplicates: totals.duplicates + part.duplicates,
      errors: totals.errors + part.errors,
    };
    progressBase.processed += chunk.length;
    progressBase.imported += part.inserted;
    progressBase.duplicates += part.duplicates;
    progressBase.errors += part.errors;
    callbacks?.onProgress?.({
      phase: 'importing',
      processed: progressBase.processed,
      total: progressBase.total,
      imported: progressBase.imported,
      duplicates: progressBase.duplicates,
      errors: progressBase.errors,
      currentSheet: sheetName,
    });
  }
  return totals;
}

export async function fetchOutreachDatabaseCounts(): Promise<OutreachDatabaseCounts | null> {
  const { data, error } = await supabase.rpc('outreach_database_counts');
  if (error || !data) {
    if (error) {
      console.warn('[outreach] fetchOutreachDatabaseCounts error:', error.message, error.code);
      if (isTransportAbortError(error)) throw error;
    }
    return null;
  }
  return data as OutreachDatabaseCounts;
}

export async function fetchImportBatches(): Promise<ImportBatchRow[]> {
  const { data, error } = await supabase
    .from('outreach_import_batches')
    .select('id, filename, sheet_name, imported_count, total_rows, created_at')
    .order('created_at', { ascending: false })
    .limit(50);
  if (error) return [];
  return (data ?? []) as ImportBatchRow[];
}

async function rpcWithDeadlockRetry<T>(
  fn: () => Promise<{ data: T | null; error: { code?: string; message: string } | null }>,
): Promise<{ data: T | null; error: { code?: string; message: string } | null }> {
  for (let attempt = 0; attempt < DEADLOCK_RETRIES; attempt++) {
    const result = await fn();
    if (!result.error) return result;
    if (isDeadlockError(result.error.code, result.error.message) && attempt < DEADLOCK_RETRIES - 1) {
      await sleep(400 * (attempt + 1));
      continue;
    }
    return result;
  }
  return { data: null, error: { message: 'Deadlock retries exhausted' } };
}

export async function wipeAllOutreachData(): Promise<{ ok: boolean; deleted?: { deleted_persons?: number }; error?: string }> {
  const { data, error } = await rpcWithDeadlockRetry(() => supabase.rpc('outreach_wipe_all'));
  if (error) return { ok: false, error: error.message };
  return { ok: true, deleted: data as { deleted_persons?: number } };
}

export async function deleteOutreachByBatch(batchId: string): Promise<{ ok: boolean; deleted?: number; error?: string }> {
  const { data, error } = await rpcWithDeadlockRetry(() =>
    supabase.rpc('outreach_delete_by_batch', { p_batch_id: batchId }),
  );
  if (error) return { ok: false, error: error.message };
  const d = data as { deleted_persons?: number };
  return { ok: true, deleted: d.deleted_persons ?? 0 };
}

export async function importOutreachFile(
  parsed: ParsedImportFile,
  options: ImportOptions = {},
  callbacks?: ImportCallbacks,
): Promise<ImportRunResult> {
  if (bulkOperationInFlight) {
    throw new Error('Another outreach import or delete is already running. Please wait for it to finish.');
  }
  bulkOperationInFlight = true;

  try {
  const databaseBefore = await fetchOutreachDatabaseCounts();
  let replacedExisting = false;
  let cancelled = false;
  const rowsInFile = parsed.sheets.reduce((n, s) => n + s.rows.length, 0);

  const progressBase = {
    processed: 0,
    total: rowsInFile,
    imported: 0,
    duplicates: 0,
    errors: 0,
  };

  callbacks?.onProgress?.({
    phase: options.replaceAll ? 'wiping' : 'loading_existing',
    ...progressBase,
  });

  let existingEmails = new Set<string>();
  let existingPhones = new Set<string>();

  if (options.replaceAll) {
    throwIfAborted(callbacks?.signal);
    const wipe = await wipeAllOutreachData();
    if (!wipe.ok) throw new Error(wipe.error ?? 'Could not clear existing outreach data.');
    replacedExisting = true;
  } else {
    throwIfAborted(callbacks?.signal);
    const keys = await fetchExistingKeys();
    existingEmails = keys.emails;
    existingPhones = keys.phones;
  }

  const sheetResults: SheetImportResult[] = [];
  let totalImported = 0;
  let totalDuplicates = 0;
  let totalSkipped = 0;
  let totalErrors = 0;

  try {
    for (const sheet of parsed.sheets) {
      throwIfAborted(callbacks?.signal);
      const totalRows = sheet.rows.length;

      const { data: batch, error: batchErr } = await supabase
        .from('outreach_import_batches')
        .insert({
          filename: parsed.filename,
          sheet_name: sheet.sheetName,
          file_format: parsed.fileFormat,
          detected_format: sheet.detectedFormat,
          column_mapping: sheet.columnMapping,
          total_rows: totalRows,
          status: 'processing',
        })
        .select('id')
        .single();

      if (batchErr || !batch?.id) {
        sheetResults.push({
          sheetName: sheet.sheetName,
          batchId: null,
          rowsInFile: totalRows,
          imported: 0,
          duplicates: 0,
          skipped: totalRows,
          errors: totalRows,
        });
        totalSkipped += totalRows;
        totalErrors += totalRows;
        progressBase.processed += totalRows;
        continue;
      }

      const batchId = batch.id as string;
      let insertRows = sheet.rows.map(r => toInsertRow(r, batchId, sheet, parsed.fileFormat));

      const { rows: fileUnique, duplicates: fileDupes } = dedupeWithinFile(insertRows);
      insertRows = fileUnique;

      let dbDupes = 0;
      if (!options.replaceAll) {
        const filtered = filterExisting(insertRows, existingEmails, existingPhones);
        insertRows = filtered.rows;
        dbDupes = filtered.duplicates;
      }

      const sheetDupesBeforeImport = fileDupes + dbDupes;
      totalDuplicates += sheetDupesBeforeImport;
      progressBase.duplicates += sheetDupesBeforeImport;
      progressBase.processed += sheetDupesBeforeImport;
      callbacks?.onProgress?.({
        phase: 'importing',
        processed: progressBase.processed,
        total: progressBase.total,
        imported: progressBase.imported,
        duplicates: progressBase.duplicates,
        errors: progressBase.errors,
        currentSheet: sheet.sheetName,
      });

      const rpcStats = await importRowsChunked(
        insertRows,
        callbacks,
        progressBase,
        sheet.sheetName,
      );

      const sheetDupes = sheetDupesBeforeImport + rpcStats.duplicates;
      const imported = rpcStats.inserted;
      const errors = rpcStats.errors;
      const skipped = Math.max(0, totalRows - imported - sheetDupes - errors);

      totalDuplicates += rpcStats.duplicates;
      totalImported += imported;
      totalErrors += errors;
      totalSkipped += skipped;

      await supabase
        .from('outreach_import_batches')
        .update({
          imported_count: imported,
          duplicate_count: sheetDupes,
          skipped_count: skipped,
          error_count: errors,
          status: callbacks?.signal?.aborted
            ? 'partial'
            : errors > 0 && imported === 0
              ? 'failed'
              : errors > 0
                ? 'partial'
                : 'completed',
          completed_at: new Date().toISOString(),
        })
        .eq('id', batchId);

      sheetResults.push({
        sheetName: sheet.sheetName,
        batchId,
        rowsInFile: totalRows,
        imported,
        duplicates: sheetDupes,
        skipped,
        errors,
      });
    }
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') {
      cancelled = true;
      callbacks?.onProgress?.({ phase: 'cancelled', ...progressBase });
    } else {
      throw e;
    }
  }

  const databaseAfter = await fetchOutreachDatabaseCounts();

  callbacks?.onProgress?.({
    phase: cancelled ? 'cancelled' : 'done',
    ...progressBase,
  });

  return {
    filename: parsed.filename,
    fileFormat: parsed.fileFormat,
    replacedExisting,
    cancelled,
    rowsInFile,
    totalImported,
    totalDuplicates,
    totalSkipped,
    totalErrors,
    sheets: sheetResults,
    databaseBefore,
    databaseAfter,
  };
  } finally {
    bulkOperationInFlight = false;
  }
}

export async function deleteOutreachPersons(
  ids: string[],
  callbacks?: { onProgress?: (p: DeleteProgress) => void; signal?: AbortSignal },
): Promise<{ deleted: number; error: string | null; cancelled: boolean }> {
  if (ids.length === 0) return { deleted: 0, error: null, cancelled: false };
  const BATCH = 100;
  let deleted = 0;
  let cancelled = false;

  for (let i = 0; i < ids.length; i += BATCH) {
    if (callbacks?.signal?.aborted) {
      cancelled = true;
      break;
    }
    const chunk = ids.slice(i, i + BATCH);
    if (chunk.length === 0) continue;

    let batchDeleted = 0;
    for (let attempt = 0; attempt < DEADLOCK_RETRIES; attempt++) {
      const { data, error } = await supabase.rpc('outreach_delete_persons_by_ids', { p_ids: chunk });
      if (!error) {
        const d = data as { deleted?: number } | null;
        batchDeleted = d?.deleted ?? 0;
        break;
      }
      if (isDeadlockError(error.code, error.message) && attempt < DEADLOCK_RETRIES - 1) {
        await sleep(400 * (attempt + 1));
        continue;
      }
      return { deleted, error: error.message, cancelled };
    }

    deleted += batchDeleted;

    callbacks?.onProgress?.({
      phase: 'deleting',
      processed: Math.min(i + BATCH, ids.length),
      total: ids.length,
      deleted,
    });
  }

  callbacks?.onProgress?.({ phase: 'done', processed: ids.length, total: ids.length, deleted });
  return { deleted, error: null, cancelled };
}

export interface OutreachKpi {
  total_persons: number;
  with_email: number;
  reachable: number;
  not_contacted: number;
  in_sequence: number;
  responded: number;
  responded_positive: number;
  responded_negative: number;
  converted: number;
  blocked: number;
  lost: number;
  ready_to_send_today: number;
}

export interface OutreachPersonRow {
  id: string;
  full_name: string | null;
  company_name: string | null;
  email: string | null;
  phone: string | null;
  person_kind: string;
  outreach_state: string;
  location: string | null;
  functional_area: string | null;
  industry: string | null;
  seniority_level: string | null;
  source_row_status: string | null;
  is_reachable: boolean;
  source_sheet: string | null;
  attributes: Record<string, unknown> | null;
  last_emailed_at: string | null;
  created_at: string;
}

export async function backfillOutreachFromAttributes(): Promise<void> {
  await supabase.rpc('outreach_backfill_from_attributes');
}

export interface SendOutreachResult {
  ok: boolean;
  sent?: number;
  skipped?: number;
  failed?: number;
  error?: string;
}

export async function sendOutreachIntroEmails(personIds: string[]): Promise<SendOutreachResult> {
  const { data, error } = await supabase.functions.invoke<SendOutreachResult & { ok: boolean }>(
    'send-outreach-email',
    { body: { person_ids: personIds.slice(0, 50) } },
  );
  if (error) return { ok: false, error: error.message };
  if (!data?.ok) return { ok: false, error: data?.error ?? 'Send failed' };
  return { ok: true, sent: data.sent, skipped: data.skipped, failed: data.failed };
}

export async function sendOutreachIntroToLeads(leadIds: string[]): Promise<SendOutreachResult> {
  const { data, error } = await supabase.functions.invoke<SendOutreachResult & { ok: boolean }>(
    'send-outreach-email',
    { body: { lead_ids: leadIds.slice(0, 50) } },
  );
  if (error) return { ok: false, error: error.message };
  if (!data?.ok) return { ok: false, error: data?.error ?? 'Send failed' };
  return { ok: true, sent: data.sent, skipped: data.skipped, failed: data.failed };
}

export async function sendOutreachIntroToUsers(userIds: string[]): Promise<SendOutreachResult> {
  const { data, error } = await supabase.functions.invoke<SendOutreachResult & { ok: boolean }>(
    'send-outreach-email',
    { body: { user_ids: userIds.slice(0, 50) } },
  );
  if (error) return { ok: false, error: error.message };
  if (!data?.ok) return { ok: false, error: data?.error ?? 'Send failed' };
  return { ok: true, sent: data.sent, skipped: data.skipped, failed: data.failed };
}

const RETARGET_DAYS_MS = 7 * 86_400_000;

export type OutreachEmailColumnFilter = 'all' | 'gmail' | 'has_email' | 'no_email' | 'bounced';

/** Status segments shown as tabs above the contacts table. */
export type OutreachStateFilter =
  | 'all' | 'new' | 'emailed' | 'responded' | 'converted' | 'bounced' | 'blocked' | 'lost';

/** Maps a status segment to the concrete outreach_state values it covers. */
export const OUTREACH_STATE_FILTER_VALUES: Record<OutreachStateFilter, string[]> = {
  all:       [],
  new:       ['not_contacted'],
  emailed:   ['queued', 'in_sequence'],
  responded: ['responded'],
  converted: ['converted'],
  bounced:   ['bounced'],
  blocked:   ['do_not_contact'],
  lost:      ['lost'],
};

export interface OutreachPersonsPageParams {
  page: number;      // 1-based
  pageSize: number;
  emailFilter?: OutreachEmailColumnFilter;
  stateFilter?: OutreachStateFilter;
}

export interface OutreachPersonsPage {
  rows: OutreachPersonRow[];
  total: number;
}

const OUTREACH_LIST_COLUMNS = `
  id, full_name, company_name, email, phone, person_kind, outreach_state,
  location, functional_area, industry, seniority_level, source_row_status,
  is_reachable, source_sheet, last_emailed_at, created_at
`;

/**
 * Server-side paginated fetch — returns only the requested page plus an exact
 * total (respecting the email filter) so the table can render page N/M without
 * ever pulling the whole 24k-row table into the browser.
 */
export async function fetchOutreachPersonsPage(
  params: OutreachPersonsPageParams,
): Promise<OutreachPersonsPage> {
  const page = Math.max(1, params.page);
  const pageSize = Math.max(1, params.pageSize);
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  let query = supabase
    .from('outreach_persons')
    .select(OUTREACH_LIST_COLUMNS, { count: 'exact' })
    .order('list_sort_priority', { ascending: true })
    .order('created_at', { ascending: false })
    .range(from, to);

  const stateValues = OUTREACH_STATE_FILTER_VALUES[params.stateFilter ?? 'all'];
  if (stateValues.length === 1) {
    query = query.eq('outreach_state', stateValues[0]);
  } else if (stateValues.length > 1) {
    query = query.in('outreach_state', stateValues);
  }

  switch (params.emailFilter) {
    case 'gmail':
      query = query.ilike('email', '%@gmail.com');
      break;
    case 'has_email':
      query = query.not('email', 'is', null).neq('email', '');
      break;
    case 'no_email':
      query = query.or('email.is.null,email.eq.');
      break;
    case 'bounced':
      query = query.eq('outreach_state', 'bounced');
      break;
    default:
      break;
  }

  const { data, error, count } = await query;
  if (error) {
    console.warn('[outreach] fetchOutreachPersonsPage error:', error.message, error.code);
    // Surface the failure so the caller can keep the last good page on screen
    // instead of blanking the table (prevents the "results come and go" flicker).
    throw new Error(error.message || 'Failed to load contacts');
  }
  const rows = (data ?? []).map(row => ({ ...row, attributes: null })) as OutreachPersonRow[];
  return { rows, total: count ?? rows.length };
}

export async function fetchOutreachPersons(limit = 500): Promise<OutreachPersonRow[]> {
  // Omit bulky attributes JSON for the list view — display columns are enough.
  const { data, error } = await supabase
    .from('outreach_persons')
    .select(`
      id, full_name, company_name, email, phone, person_kind, outreach_state,
      location, functional_area, industry, seniority_level, source_row_status,
      is_reachable, source_sheet, last_emailed_at, created_at
    `)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) {
    console.warn('[outreach] fetchOutreachPersons error:', error.message, error.code);
    if (isTransportAbortError(error)) throw error;
    return [];
  }
  return (data ?? []).map(row => ({ ...row, attributes: null })) as OutreachPersonRow[];
}

/** Contacts emailed 7+ days ago — due for a follow-up nudge. */
export async function fetchRetargetingPersons(limit = 500): Promise<OutreachPersonRow[]> {
  const cutoff = new Date(Date.now() - RETARGET_DAYS_MS).toISOString();
  const { data, error } = await supabase
    .from('outreach_persons')
    .select(`
      id, full_name, company_name, email, phone, person_kind, outreach_state,
      location, functional_area, industry, seniority_level, source_row_status,
      is_reachable, source_sheet, last_emailed_at, created_at
    `)
    .not('last_emailed_at', 'is', null)
    .lt('last_emailed_at', cutoff)
    .eq('is_reachable', true)
    .not('outreach_state', 'eq', 'converted')
    .not('outreach_state', 'eq', 'do_not_contact')
    .order('last_emailed_at', { ascending: true })
    .limit(limit);
  if (error) {
    if (isTransportAbortError(error)) throw error;
    return [];
  }
  return (data ?? []).map(row => ({ ...row, attributes: null })) as OutreachPersonRow[];
}

export async function fetchOutreachKpi(): Promise<OutreachKpi | null> {
  const { data, error } = await supabase.from('outreach_v_kpi_summary').select('*').maybeSingle();
  if (error) {
    console.warn('[outreach] fetchOutreachKpi error:', error.message, error.code);
    if (isTransportAbortError(error)) throw error;
    return null;
  }
  return data as OutreachKpi;
}

export interface OutreachEmailStats {
  total_emails: number;
  accepted: number;
  delivered: number;
  engaged: number;
  bounced: number;
  failed: number;
  pending: number;
}

export interface OutreachEmailLogRow {
  touchpoint_id: string;
  person_id: string;
  full_name: string | null;
  email: string | null;
  subject: string | null;
  delivery_status: string;
  sent_at: string | null;
  opened_at: string | null;
  clicked_at: string | null;
  bounced_at: string | null;
  resend_message_id: string | null;
  error_message: string | null;
  template_key: string | null;
  created_at: string;
}

export interface OutreachPersonLastEmail {
  person_id: string;
  last_email_status: string;
  last_email_subject: string | null;
  last_email_sent_at: string | null;
}

export async function fetchOutreachEmailStats(): Promise<OutreachEmailStats | null> {
  const { data, error } = await supabase.from('outreach_v_email_stats').select('*').maybeSingle();
  if (error || !data) return null;
  return data as OutreachEmailStats;
}

export async function fetchOutreachEmailLog(limit = 200): Promise<OutreachEmailLogRow[]> {
  const { data, error } = await supabase
    .from('outreach_v_email_log')
    .select(`
      touchpoint_id, person_id, full_name, email, subject, delivery_status,
      sent_at, opened_at, clicked_at, bounced_at, resend_message_id,
      error_message, template_key, created_at
    `)
    .order('sent_at', { ascending: false, nullsFirst: false })
    .limit(limit);
  if (error) return [];
  return (data ?? []) as OutreachEmailLogRow[];
}

export async function fetchOutreachPersonLastEmails(
  personIds?: string[],
): Promise<Map<string, OutreachPersonLastEmail>> {
  const map = new Map<string, OutreachPersonLastEmail>();
  // Never pull the full view unbounded — scope to the rows on screen.
  if (personIds && personIds.length === 0) return map;

  if (personIds?.length) {
    // PostgREST .in() has URL length limits; chunk large id lists.
    const CHUNK = 200;
    for (let i = 0; i < personIds.length; i += CHUNK) {
      const chunk = personIds.slice(i, i + CHUNK);
      const { data, error } = await supabase
        .from('outreach_v_person_last_email')
        .select('person_id, last_email_status, last_email_subject, last_email_sent_at')
        .in('person_id', chunk);
      if (error) {
        if (isTransportAbortError(error)) throw error;
        break;
      }
      for (const row of (data ?? []) as OutreachPersonLastEmail[]) {
        map.set(row.person_id, row);
      }
    }
    return map;
  }

  const { data, error } = await supabase
    .from('outreach_v_person_last_email')
    .select('person_id, last_email_status, last_email_subject, last_email_sent_at')
    .limit(500);
  if (error) {
    if (isTransportAbortError(error)) throw error;
    return map;
  }
  for (const row of (data ?? []) as OutreachPersonLastEmail[]) {
    map.set(row.person_id, row);
  }
  return map;
}
